/* eslint react-hooks/exhaustive-deps: "warn" */
import { type FormEvent, useRef, useState } from 'react';

import cn from 'classnames';

import { isNotBusinessEmail } from 'libs/string';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Button } from 'ui/atoms/Button/Button';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './MsvFinalCta.module.scss';

type Translate = (key: string) => string;

const getSteps = (t: Translate) => [
  {
    num: '01',
    title: t('Send a sample'),
    text: t('A CSV of sellers you already onboarded — name, country, registration number if you have it.'),
  },
  {
    num: '02',
    title: t('We resolve each row'),
    text: t('Every seller matched to its primary registry position, with the source behind each field.'),
  },
  {
    num: '03',
    title: t('You see the surprises'),
    text: t('Dissolved, mismatched, or sanctioned entities flagged. They usually land in the first hundred rows.'),
  },
];

const ArrowIcon = () => (
  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true">
    <path
      d="M2.6 8h10M8.4 3.8 12.6 8l-4.2 4.2"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const MsvFinalCta = () => {
  const { t } = useInt();
  const ref = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(ref, { threshold: 0.15 });

  const steps = getSteps(t);

  const [email, setEmail] = useState('');
  const [count, setCount] = useState('');
  const [attempted, setAttempted] = useState(false);
  const [sent, setSent] = useState(false);

  const emailInvalid = !email.trim() || isNotBusinessEmail(email);
  const emailError = attempted && emailInvalid ? t('Enter a business email address') : undefined;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    setAttempted(true);

    if (emailInvalid) {
      return;
    }

    setSent(true);
  };

  return (
    <Section className={styles.root}>
      <div ref={ref} className={cn(styles.grid, styles.reveal, revealed && styles.revealed)}>
        <div className={styles.copy}>
          <TitleBlock
            align="left"
            title={t('Run your seller file through it.')}
            description={t(
              'Send a sample of sellers you already onboarded. We return the registry position on each, with sources. The surprises usually land in the first hundred rows.',
            )}
          />

          <ol className={styles.steps}>
            {steps.map((step) => (
              <li key={step.num} className={styles.step}>
                <span className={styles.stepNum}>{step.num}</span>
                <div className={styles.stepBody}>
                  <span className={styles.stepTitle}>{step.title}</span>
                  <p className={styles.stepText}>{step.text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className={styles.card}>
          <div className={styles.cardBar}>
            <span className={styles.cardFile}>seller-sample.csv</span>
            <span className={styles.cardLive}>
              <span className={styles.pulse} />
              Ready
            </span>
          </div>

          {!sent ? (
            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <label className={styles.field}>
                <span className={styles.label}>{t('Work email')}</span>
                <input
                  className={cn(styles.input, emailError && styles.inputError)}
                  type="email"
                  name="email"
                  autoComplete="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {emailError && <span className={styles.errorText}>{emailError}</span>}
              </label>

              <label className={styles.field}>
                <span className={styles.label}>
                  {t('Rows in your sample')} <span className={styles.optional}>{t('optional')}</span>
                </span>
                <input
                  className={styles.input}
                  type="text"
                  name="count"
                  placeholder={t('e.g. 250 sellers')}
                  value={count}
                  onChange={(e) => setCount(e.target.value)}
                />
              </label>

              <Button htmlType="submit" type="blue" size="xl" width="100%" suffix={<ArrowIcon />}>
                {t('Return the registry position')}
              </Button>

              <p className={styles.fine}>
                {t('A named person replies with a scoped run against your rows. No sequence, no drip.')}
              </p>
            </form>
          ) : (
            <div className={styles.done}>
              <span className={styles.doneCheck} aria-hidden="true">
                ✓
              </span>
              <div className={styles.doneTitle}>{t('Sample received.')}</div>
              <p className={styles.doneText}>
                {t(
                  "We'll run it against the registry record and send the resolved file, with sources on every row, to",
                )}{' '}
                <b>{email}</b>.
              </p>
            </div>
          )}
        </div>
      </div>
    </Section>
  );
};
